import { nextTick, type Ref } from 'vue';
import type { CharacterTimelinePoint } from '../types/timeline';
import type { EntityDocument, EntitySummary, EntityTemplate } from '../types/entities';
import type { WorkspaceSaveState } from '../types/workspace';
import type { useWorkspaceStore } from '../stores/workspace';

type TimeWorkspaceView = 'timeline' | 'scene-timeline' | 'character-timeline' | 'family-relationships' | 'relationship-graph';

export function useWorkspaceNavigation(options: {
  store: ReturnType<typeof useWorkspaceStore>;
  flushSave: () => Promise<void>;
  hydrateDraft: () => void;
  revealEntityInCategory: (id: string) => void;
  addEntityTab: (id: string) => void;
  addUtilityTab: (kind: 'settings' | 'prototype' | 'timeline') => void;
  saveState: Readonly<Ref<WorkspaceSaveState>>;
  view: Ref<'workspace' | 'settings' | TimeWorkspaceView | 'prototype'>;
  activeEntity: Readonly<Ref<EntityDocument | null>>;
  activeTemplate: Readonly<Ref<EntityTemplate | null | undefined>>;
  aiAssistantOpen: Readonly<Ref<boolean>>;
  canEditWorkspace: Readonly<Ref<boolean>>;
  settingsTemplateId: Ref<string | null>;
  settingsCategoryId: Ref<string | null>;
  templateMenuId: Ref<string | null>;
  categoryMenuId: Ref<string | null>;
  returnChapterId: Ref<string | null>;
  isChapter: Readonly<Ref<boolean>>;
  isVersionReadonly: Readonly<Ref<boolean>>;
  templates: Readonly<Ref<EntityTemplate[]>>;
  entities: Readonly<Ref<EntitySummary[]>>;
  chapterScenes: Readonly<Ref<EntitySummary[]>>;
  newEntity: { templateId: string; title: string; fieldValues: Record<string, unknown> };
  pendingNewSceneChapterId: Ref<string | null>;
  newEntityExplorerParentId: Ref<string | null | 'none'>;
  createStep: Ref<1 | 2>;
  createOpen: Ref<boolean>;
  prototypeScopeKind: Ref<'story' | 'chapter' | 'scene'>;
  prototypeScopeEntityId: Ref<string | null>;
  familyRelationshipGroupId: Ref<string | null>;
  lastTimeWorkspace: Ref<TimeWorkspaceView>;
}) {
  const { store, flushSave, hydrateDraft, revealEntityInCategory, addEntityTab, addUtilityTab, saveState, view, activeEntity, activeTemplate, aiAssistantOpen, canEditWorkspace, settingsTemplateId, settingsCategoryId, templateMenuId, categoryMenuId, returnChapterId, isChapter, isVersionReadonly, templates, entities, chapterScenes, newEntity, pendingNewSceneChapterId, newEntityExplorerParentId, createStep, createOpen, prototypeScopeKind, prototypeScopeEntityId, familyRelationshipGroupId, lastTimeWorkspace } = options;
async function selectEntity(id: string, trackTab = true) {
  await flushSave();
  if (saveState.value === 'conflict') return;
  await store.checkpointActive().catch(() => undefined);
  view.value = 'workspace';
  revealEntityInCategory(id);
  await store.openEntity(id);
  const opened = activeEntity.value;
  if (opened && aiAssistantOpen.value) {
    const aiUrl = String(opened.workingVersion.fieldValues.ai_url ?? opened.workingVersion.fieldValues.aiUrl ?? '').trim();
    window.dispatchEvent(new CustomEvent('chronis-ai-companion-sync', {
      detail: { entity: aiUrl ? { id: opened.id, title: opened.title, template: activeTemplate.value?.name ?? 'Entity', aiUrl } : null, stale: !aiUrl }
    }));
  }
  if (trackTab) addEntityTab(id);
}

async function openSceneFromChapter(id: string) {
  if (activeEntity.value?.templateCategory === 'kapitel') returnChapterId.value = activeEntity.value.id;
  await selectEntity(id);
}

async function returnToChapter() {
  const id = returnChapterId.value;
  returnChapterId.value = null;
  if (id) await selectEntity(id);
}

async function openSettings(addTab = true, templateId: string | null = null, categoryId: string | null = null) {
  if (!canEditWorkspace.value) return;
  await flushSave();
  if (saveState.value === 'conflict') return;
  await store.checkpointActive().catch(() => undefined);
  settingsTemplateId.value = templateId;
  settingsCategoryId.value = categoryId;
  view.value = 'settings';
  if (addTab) addUtilityTab('settings');
}

async function openTemplateSettings(templateId: string) {
  templateMenuId.value = null;
  await openSettings(true, templateId);
}

async function openCategorySettings(categoryId: string) {
  categoryMenuId.value = null;
  await openSettings(true, null, categoryId);
}

async function openTimeline() {
  await flushSave();
  if (saveState.value === 'conflict') return;
  await Promise.all([store.loadTimeline(), store.loadCharacterTimeline()]);
  view.value = 'timeline';
  rememberTimeWorkspace('timeline');
}

async function openSceneTimeline() {
  await flushSave();
  if (saveState.value === 'conflict') return;
  await Promise.all([store.loadEntities(), store.loadCharacterTimeline()]);
  view.value = 'scene-timeline';
  rememberTimeWorkspace('scene-timeline');
}

async function createSceneForChapter() {
  if (!activeEntity.value || !isChapter.value || isVersionReadonly.value) return;
  const sceneTemplate = templates.value.find((template) => template.category === 'szene' && template.key === 'szene')
    ?? templates.value.find((template) => template.category === 'szene');
  if (!sceneTemplate) { store.error = 'Es gibt noch kein Szenen-Template.'; return; }
  newEntity.templateId = sceneTemplate.id;
  await nextTick();
  newEntity.title = '';
  newEntity.fieldValues = { ...newEntity.fieldValues };
  pendingNewSceneChapterId.value = activeEntity.value.id;
  newEntityExplorerParentId.value = 'none';
  createStep.value = 2;
  createOpen.value = true;
}

async function openRapidPrototype(addTab = true) {
  await flushSave();
  if (saveState.value === 'conflict') return;
  await store.checkpointActive().catch(() => undefined);
  if (activeEntity.value?.templateCategory === 'kapitel') {
    prototypeScopeKind.value = 'chapter';
    prototypeScopeEntityId.value = activeEntity.value.id;
  } else if (activeEntity.value?.templateCategory === 'szene') {
    prototypeScopeKind.value = 'scene';
    prototypeScopeEntityId.value = activeEntity.value.id;
  } else {
    prototypeScopeKind.value = 'story';
    prototypeScopeEntityId.value = null;
  }
  await store.loadPrototypeBlocks();
  view.value = 'prototype';
  if (addTab) addUtilityTab('prototype');
}
async function attachSceneToChapter(sceneId: string) {
  if (!activeEntity.value || !isChapter.value || isVersionReadonly.value) return;
  const scene = entities.value.find((item) => item.id === sceneId); if (!scene) return;
  const storyline = String(scene.fieldValues?.storyline || 'Haupthandlung');
  try { await store.reorderSceneTimeline([...chapterScenes.value.filter((item) => String(item.fieldValues?.storyline || 'Haupthandlung') === storyline).map((item) => item.id), sceneId], storyline, activeEntity.value.id); }
  catch (reason) { store.error = reason instanceof Error ? reason.message : 'Szene konnte nicht dem Kapitel zugeordnet werden.'; }
}

async function openCharacterTimeline() {
  await flushSave();
  if (saveState.value === 'conflict') return;
  await store.loadCharacterTimeline();
  view.value = 'character-timeline';
  rememberTimeWorkspace('character-timeline');
}

async function openRelationshipGraph() {
  await flushSave();
  if (saveState.value === 'conflict') return;
  await store.loadCharacterTimeline();
  view.value = 'relationship-graph';
  rememberTimeWorkspace('relationship-graph');
}

async function openFamilyRelationships(groupId: string | null = null) {
  await flushSave();
  if (saveState.value === 'conflict') return;
  await store.loadCharacterTimeline();
  familyRelationshipGroupId.value = groupId;
  view.value = 'family-relationships';
  rememberTimeWorkspace('family-relationships');
}

function rememberTimeWorkspace(target: TimeWorkspaceView) { lastTimeWorkspace.value = target; localStorage.setItem('chronis.workspace.time-view.v1', target); }
async function openTimeWorkspace(target: TimeWorkspaceView = lastTimeWorkspace.value, addTab = true) {
  if (addTab) addUtilityTab('timeline');
  if (target === 'timeline') return openTimeline();
  if (target === 'scene-timeline') return openSceneTimeline();
  if (target === 'character-timeline') return openCharacterTimeline();
  if (target === 'family-relationships') return openFamilyRelationships(null);
  return openRelationshipGraph();
}

function closeUtilityView() {
  view.value = 'workspace';
  hydrateDraft();
}

async function openCharacterTimelinePoint(point: CharacterTimelinePoint) {
  await selectEntity(point.sceneId);
  if (!point.segmentId) return;
  await nextTick();
  document.querySelector<HTMLElement>(`[data-segment-id="${point.segmentId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
  return { selectEntity, openSceneFromChapter, returnToChapter, openSettings, openTemplateSettings, openCategorySettings, openTimeline, openSceneTimeline, createSceneForChapter, openRapidPrototype, attachSceneToChapter, openCharacterTimeline, openRelationshipGraph, openFamilyRelationships, rememberTimeWorkspace, openTimeWorkspace, closeUtilityView, openCharacterTimelinePoint };
}
